/* ==========================================================================
   PAMPER & PEACE — scent-quiz-popup.js
   Welcome pop-up that hosts the scent quiz. Greets a first-time visitor, shows
   an intro, then reveals the full quiz (wired by scent-quiz.js) inside the
   dialog. Handles trigger timing, "show once / per session / every N days"
   frequency, focus trapping, and lazy-loading the radar visualiser when opened.

   Config is read from data-* on [data-quiz-popup]; behaviour is authored in the
   section schema so merchants can tune it without touching code.
   ========================================================================== */
(function () {
  'use strict';
  const Pamper = (window.Pamper = window.Pamper || {});

  const popup = document.querySelector('[data-quiz-popup]');
  if (!popup) return;

  const dialog = popup.querySelector('[data-qpop-dialog]');
  const intro = popup.querySelector('[data-qpop-intro]');
  const quizWrap = popup.querySelector('[data-qpop-quiz]');
  const startBtn = popup.querySelector('[data-qpop-start]');
  const closers = popup.querySelectorAll('[data-qpop-close]');
  // The floating launcher lives outside the modal (sibling in the section).
  const launchers = document.querySelectorAll('[data-qpop-launcher]');
  const autoOpen = popup.dataset.auto !== 'false';

  // parseInt(...) || fallback would turn a legit 0 (e.g. a 0s delay) into the
  // fallback, so keep 0 when it's what the merchant chose.
  const int = (v, d) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? d : n;
  };
  const cfg = {
    trigger: popup.dataset.trigger || 'delay', // delay | exit | scroll | immediate
    delay: int(popup.dataset.delay, 5), // seconds
    scroll: int(popup.dataset.scroll, 35), // percent
    frequency: popup.dataset.frequency || 'once', // always | session | minutes | days | once
    minutes: int(popup.dataset.minutes, 30),
    days: int(popup.dataset.days, 14),
  };

  const SEEN_KEY = 'pp:quiz-popup-seen';
  const DONE_KEY = 'pp:quiz-popup-done';
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- storage helpers (fail-safe) ---------- */
  function get(store, key) {
    try {
      return store.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function set(store, key, val) {
    try {
      store.setItem(key, val);
    } catch (e) {}
  }

  // Should the pop-up be suppressed on this load? Governed by the "Show again"
  // cadence — the recurring options re-show once their interval elapses.
  function alreadyHandled() {
    // 'always' — greet on every load; never suppressed.
    if (cfg.frequency === 'always') return false;

    // 'once' — one time ever. Dismissing OR completing counts, and a prior quiz
    // completion / subscription (pp:quiz-email) also means they're already sorted.
    if (cfg.frequency === 'once') {
      return !!(get(localStorage, SEEN_KEY) || get(localStorage, DONE_KEY) || get(localStorage, 'pp:quiz-email'));
    }

    // 'session' — once per browser session.
    if (cfg.frequency === 'session') return !!get(sessionStorage, SEEN_KEY);

    // 'minutes' / 'days' — re-show once the chosen interval has elapsed since the
    // pop-up was last shown.
    const ts = parseInt(get(localStorage, SEEN_KEY), 10);
    if (!ts) return false;
    const intervalMs = cfg.frequency === 'minutes' ? cfg.minutes * 60000 : cfg.days * 86400000;
    return Date.now() - ts < intervalMs;
  }

  function markSeen() {
    if (cfg.frequency === 'session') set(sessionStorage, SEEN_KEY, '1');
    else set(localStorage, SEEN_KEY, String(Date.now()));
  }

  /* ---------- lazy-load the inline-SVG radar visualiser when opened ---------- */
  let assetsRequested = false;
  function loadAsset(src) {
    if (!src) return;
    if ([...document.scripts].some((s) => s.src === src)) return;
    const el = document.createElement('script');
    el.src = src;
    el.defer = true;
    document.head.appendChild(el);
  }
  function loadResultAssets() {
    if (assetsRequested) return;
    assetsRequested = true;
    loadAsset(popup.dataset.visualizerSrc);
  }

  /* ---------- open / close ---------- */
  let releaseTrap;
  let lastFocus;
  let isOpen = false;

  function open(preview) {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement; // restore on close
    popup.hidden = false;
    // next frame so the transition runs from the hidden state
    requestAnimationFrame(() => popup.setAttribute('data-open', 'true'));
    document.body.style.overflow = 'hidden';
    loadResultAssets();
    if (!designMode && !preview) markSeen();
    if (Pamper.utils && Pamper.utils.trapFocus) releaseTrap = Pamper.utils.trapFocus(dialog);
    // Focus the primary action (or the close button if the quiz is already showing).
    const focusEl = (intro && !intro.hidden && startBtn) || popup.querySelector('[data-qpop-close]');
    requestAnimationFrame(() => focusEl && focusEl.focus());
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    popup.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    if (releaseTrap) releaseTrap();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    if (!designMode) markSeen();
    const finish = () => {
      popup.hidden = true;
      dialog.removeEventListener('transitionend', finish);
    };
    // Wait for the fade-out unless motion is reduced.
    if (document.documentElement.classList.contains('no-anim')) finish();
    else dialog.addEventListener('transitionend', finish);
  }

  /* ---------- intro -> quiz ---------- */
  function startQuiz() {
    if (intro) intro.hidden = true;
    if (quizWrap) {
      quizWrap.hidden = false;
      // Ensure the quiz is wired (it usually auto-inits on load) and focus its
      // first question for keyboard users.
      const root = quizWrap.querySelector('[data-quiz]');
      if (root && Pamper.initQuiz) Pamper.initQuiz(root);
      const legend = quizWrap.querySelector('.quiz__step[data-active="true"] .quiz__q');
      if (legend) requestAnimationFrame(() => legend.focus({ preventScroll: true }));
    }
  }

  /* ---------- wiring ---------- */
  if (startBtn) startBtn.addEventListener('click', startQuiz);
  closers.forEach((c) => c.addEventListener('click', close));
  // The launcher is a deliberate action, so it always opens — no frequency guard.
  launchers.forEach((l) => l.addEventListener('click', () => open(true)));

  // On the product page, lift the launcher just above the sticky buy bar while
  // it's showing so it never sits on top of Add-to-cart / Buy-now. The bar slides
  // in/out via an `is-visible` class (see product-pdp.liquid); we mirror that with
  // a --qpop-lift custom property the launcher CSS adds to its bottom offset.
  if (launchers.length) {
    const bar = document.querySelector('[data-sticky-bar]');
    if (bar) {
      const GAP = 12;
      const syncLift = () => {
        const lift = bar.classList.contains('is-visible') ? bar.offsetHeight + GAP : 0;
        launchers.forEach((l) => l.style.setProperty('--qpop-lift', lift + 'px'));
      };
      syncLift();
      new MutationObserver(syncLift).observe(bar, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
      let rt;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(rt);
          rt = setTimeout(syncLift, 150);
        },
        { passive: true }
      );
    }
  }
  popup.addEventListener('click', (e) => {
    if (e.target === popup || e.target.hasAttribute('data-qpop-overlay')) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });
  // Finding a match counts as "sorted" — remember it so we never nag again.
  popup.addEventListener('pp:quiz-reveal', () => set(localStorage, DONE_KEY, '1'));
  // Adding the matched scent hands off to the cart drawer — close so it's visible.
  // (We don't preventDefault; the global cart handler still runs.)
  popup.addEventListener('submit', (e) => {
    if (e.target.closest('[data-quick-add-form]')) close();
  });

  /* ---------- preview / reset via URL ----------
     ?quiz-popup=1 (or =open) force-opens it regardless of the frequency guard
     — and even inside the theme editor — without counting as "seen"; handy for
     previewing/QA. ?quiz-popup=reset clears the guard so the normal trigger
     fires again on the next load. Checked before designMode so it always works. */
  const force = new URLSearchParams(location.search).get('quiz-popup');
  if (force === 'reset') {
    try {
      localStorage.removeItem(SEEN_KEY);
      localStorage.removeItem(DONE_KEY);
      sessionStorage.removeItem(SEEN_KEY);
    } catch (e) {}
  }
  if (force === '1' || force === 'open') {
    open(true);
    return;
  }

  /* ---------- theme editor: preview on select, no auto-pop ---------- */
  if (designMode) {
    const sid = popup.dataset.sectionId;
    document.addEventListener('shopify:section:select', (e) => {
      if (e.detail && e.detail.sectionId === sid) open();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.detail && e.detail.sectionId === sid) close();
    });
    return; // never auto-trigger while editing
  }

  /* ---------- triggers ---------- */
  // Launcher-only mode: the button is wired above; skip all auto-open triggers.
  if (!autoOpen) return;
  if (alreadyHandled()) return;

  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  let armed = true;
  function fire() {
    if (!armed) return;
    armed = false;
    open();
  }

  if (cfg.trigger === 'immediate') {
    fire();
  } else if (cfg.trigger === 'scroll') {
    const onScroll = () => {
      const h = document.documentElement;
      const pct = (h.scrollTop / (h.scrollHeight - h.clientHeight || 1)) * 100;
      if (pct >= cfg.scroll) {
        window.removeEventListener('scroll', onScroll);
        fire();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  } else if (cfg.trigger === 'exit' && fine) {
    const onLeave = (e) => {
      if (e.clientY <= 0) {
        document.removeEventListener('mouseout', onLeave);
        fire();
      }
    };
    document.addEventListener('mouseout', onLeave);
  } else {
    // 'delay' — and the mobile fallback for exit-intent (no fine pointer).
    setTimeout(fire, cfg.delay * 1000);
  }
})();
